/**
 * Weekly Shape — hafte ki shaakh: average hours per day-of-week as a
 * horizontal cadence. Sleep-debt + weekend swing as marginalia.
 *
 * Registers as <aramgah-weekly-shape>.
 */
import { store } from '../store.ts';
import type { SleepNight } from '../types.ts';

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;

export class AramgahWeeklyShape extends HTMLElement {
  #shadow = this.attachShadow({ mode: 'open' });
  #unsub?: () => void;

  connectedCallback(): void {
    this.#unsub = store.subscribe((s) => {
      if (s.summary) this.#render(s.summary.nights, s.summary.insights.weekend_vs_weekday_hours_diff, s.summary.insights.sleep_debt_hours);
    });
  }
  disconnectedCallback(): void { this.#unsub?.(); }

  #render(nights: SleepNight[], weekendDiff: number, debt: number): void {
    if (!nights.length) { this.#shadow.innerHTML = '<style>:host{display:none;}</style>'; return; }
    const byDay: number[][] = Array.from({ length: 7 }, () => []);
    for (const n of nights) {
      const d = new Date(n.date + 'T00:00:00Z').getUTCDay();
      byDay[d]?.push(n.duration_hours);
    }
    const avgs = byDay.map((arr) => (arr.length ? arr.reduce((s, v) => s + v, 0) / arr.length : 0));
    const max = Math.max(...avgs, 8);

    const rows = avgs.map((a, i) => {
      const pct = max > 0 ? (a / max) * 100 : 0;
      const good = a >= 7;
      return /* html */ `
<div class="row" style="--pct:${pct}%; --delay:${i * 60}ms">
  <div class="day">${DAYS[i]}</div>
  <div class="bar-wrap"><div class="bar ${good ? 'good' : ''}"></div></div>
  <div class="val">${a > 0 ? a.toFixed(1) + 'h' : '—'}</div>
</div>`;
    }).join('');

    const diffStr = weekendDiff > 0 ? `+${weekendDiff.toFixed(2)}h weekend` : `${weekendDiff.toFixed(2)}h weekend`;
    const debtTone = debt > 15 ? 'var(--rose)' : debt > 7 ? 'var(--gold)' : 'var(--moon-cream)';

    this.#shadow.innerHTML = /* html */ `
<style>
  :host { display: block; container-type: inline-size; }
  .head {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: 10px;
    margin-bottom: 14px;
    padding-left: 4px;
    flex-wrap: wrap;
  }
  .eyebrow {
    font-family: var(--mono);
    font-size: 10px;
    letter-spacing: 0.4em;
    text-transform: uppercase;
    color: var(--ink-faint);
  }
  .head-aside {
    font-family: var(--display);
    font-style: italic;
    font-size: 14px;
    color: var(--ink-dim);
  }
  .card {
    background: var(--paper-up);
    border: 1px solid var(--rule);
    border-radius: 12px;
    padding: 22px 20px 26px;
    position: relative;
    overflow: hidden;
  }
  .card::before {
    content: '';
    position: absolute;
    inset: 0;
    background: radial-gradient(ellipse 60% 30% at 80% 100%, oklch(91% 0.04 80 / 0.05), transparent 60%);
    pointer-events: none;
  }
  .rows { display: grid; gap: 8px; position: relative; z-index: 1; }
  .row {
    display: grid;
    grid-template-columns: 50px 1fr 70px;
    gap: 12px;
    align-items: center;
  }
  .day {
    font-family: var(--mono);
    font-size: 10px;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    color: var(--ink-dim);
  }
  .bar-wrap {
    height: 8px;
    background: oklch(20% 0.04 270);
    border-radius: 999px;
    overflow: hidden;
    border: 1px solid var(--rule-faint);
  }
  .bar {
    height: 100%;
    width: var(--pct);
    background: linear-gradient(90deg, var(--moon-dim), var(--moon));
    border-radius: 999px;
    transform-origin: left;
    animation: bar-grow 760ms var(--ease-suite, cubic-bezier(0.22, 1, 0.36, 1)) both;
    animation-delay: var(--delay);
  }
  .bar.good {
    background: linear-gradient(90deg, oklch(82% 0.10 90), oklch(91% 0.13 88));
    box-shadow: 0 0 14px oklch(91% 0.04 80 / 0.25);
  }
  .val {
    font-family: var(--display);
    font-size: 19px;
    text-align: right;
    font-weight: 600;
    font-feature-settings: 'tnum' 1, 'ss01' 1;
    color: var(--moon-cream);
  }
  .foot {
    display: flex;
    justify-content: space-between;
    align-items: end;
    gap: 14px;
    margin-top: 22px;
    padding-top: 16px;
    border-top: 1px dashed var(--rule);
    position: relative;
    z-index: 1;
  }
  .foot-cell .k {
    font-family: var(--mono);
    font-size: 9px;
    letter-spacing: 0.35em;
    text-transform: uppercase;
    color: var(--ink-faint);
  }
  .foot-cell .v {
    font-family: var(--display);
    font-size: 24px;
    font-weight: 600;
    font-feature-settings: 'tnum' 1, 'ss01' 1;
    margin-top: 6px;
  }
  @keyframes bar-grow {
    from { transform: scaleX(0); opacity: 0; }
    to   { transform: scaleX(1); opacity: 1; }
  }
  @media (prefers-reduced-motion: reduce) {
    .bar { animation: none; }
  }
</style>
<div class="head">
  <div class="eyebrow">Hafte ki shaakh</div>
  <div class="head-aside">days of the week · personal cadence</div>
</div>
<div class="card">
  <div class="rows">${rows}</div>
  <div class="foot">
    <div class="foot-cell"><div class="k">Weekend swing</div><div class="v" style="color:var(--moon-cream);">${diffStr}</div></div>
    <div class="foot-cell" style="text-align:right;"><div class="k">Sleep debt</div><div class="v" style="color:${debtTone};">${debt.toFixed(1)}h</div></div>
  </div>
</div>
`;
  }
}

customElements.define('aramgah-weekly-shape', AramgahWeeklyShape);
